import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Bell, Trash2, ShoppingBag, MessageCircle, Eye, X, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminNotifications() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);

  // Load notifications from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('sizzlebox_notifications');
    if (stored) {
      try {
        setNotifications(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse notifications:', e);
      }
    }
  }, []);

  // Listen for new notifications via WebSocket
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl || !token) return;

    let ws = null;
    let reconnectTimeout = null;
    let isUnmounted = false;

    const connect = () => {
      if (isUnmounted) return;
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_order') {
            const order = data.order;
            const notif = {
              id: Date.now(),
              type: 'new_order',
              order_number: order?.order_number,
              order_id: order?.id,
              total: order?.total,
              customer: order?.customer_name,
              customer_phone: order?.customer_phone,
              items_count: order?.items?.length || 0,
              items: order?.items || [],
              address: order?.customer_address,
              payment_method: order?.payment_method,
              whatsapp_url: order?.whatsapp_notify_url || null,
              created_at: new Date().toISOString(),
            };
            setNotifications(prev => {
              const updated = [notif, ...prev].slice(0, 200);
              localStorage.setItem('sizzlebox_notifications', JSON.stringify(updated));
              return updated;
            });
          }
        } catch (e) {
          console.error('WS error:', e);
        }
      };
      ws.onclose = () => {
        if (!isUnmounted) reconnectTimeout = setTimeout(connect, 2000);
      };
      ws.onerror = () => ws?.close();
    };
    connect();
    return () => { isUnmounted = true; clearTimeout(reconnectTimeout); ws?.close(); };
  }, [token]);

  const deleteNotif = (id) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== id);
      localStorage.setItem('sizzlebox_notifications', JSON.stringify(updated));
      return updated;
    });
  };

  const clearAll = () => {
    if (!window.confirm('Clear all notifications?')) return;
    setNotifications([]);
    localStorage.removeItem('sizzlebox_notifications');
    toast.success('All notifications cleared');
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout title="Notifications" subtitle="Order notification history">
      <div data-testid="admin-notifications-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-400">{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</p>
          {notifications.length > 0 && (
            <Button variant="outline" onClick={clearAll} className="border-white/10 text-gray-400 hover:text-red-400 hover:border-red-500/30 rounded-full text-sm" data-testid="clear-all-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Clear All
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="bg-card rounded-2xl border border-white/10 p-16 text-center">
            <Bell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2 font-heading">No Notifications</h3>
            <p className="text-gray-400">New order notifications will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(notif => (
              <div key={notif.id} className="bg-card rounded-xl border border-white/10 p-5 hover:bg-white/[0.02] transition-colors" data-testid={`notif-item-${notif.id}`}>
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full bg-fire-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShoppingBag className="w-5 h-5 text-fire-500" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-bold">New Order #{notif.order_number}</h3>
                      <span className="text-fire-500 font-bold text-lg">₦{notif.total?.toLocaleString()}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mb-2">
                      <span>Customer: <span className="text-white">{notif.customer}</span></span>
                      <span>Phone: <span className="text-white">{notif.customer_phone}</span></span>
                      <span>Payment: <span className="text-white capitalize">{notif.payment_method}</span></span>
                    </div>

                    {/* Items */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {notif.items?.slice(0, 4).map((item, i) => (
                        <span key={i} className="bg-black/30 text-gray-300 text-xs px-2 py-1 rounded-full">
                          {item.quantity}x {item.name}
                        </span>
                      ))}
                      {notif.items_count > 4 && (
                        <span className="text-gray-500 text-xs py-1">+{notif.items_count - 4} more</span>
                      )}
                    </div>

                    {/* Address */}
                    {notif.address && (
                      <p className="text-gray-500 text-xs mb-2">Address: {notif.address}</p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/admin/orders/${notif.order_id}`}
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <Eye className="w-3 h-3" /> View Order
                      </Link>
                      {notif.whatsapp_url && (
                        <a
                          href={notif.whatsapp_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#25D366] hover:text-[#128C7E] transition-colors"
                        >
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Time & Delete */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="text-gray-500 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatDate(notif.created_at)}
                    </span>
                    <button
                      onClick={() => deleteNotif(notif.id)}
                      className="text-gray-600 hover:text-red-400 transition-colors p-1"
                      data-testid={`delete-notif-${notif.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
